/* GERADO por scripts/gen-budgets.ts a partir de docs/budgets/PERF.yaml.
 * NEVER edite à mão: `pnpm gen:check` falha o CI se este arquivo divergir da fonte.
 */

export const PERF_VERSION = 7;

export type TierKey = 'low' | 'mid' | 'high';
export const TIER_KEYS: readonly TierKey[] = ['low', 'mid', 'high'];

export interface TierSpec {
  readonly id: string;
  readonly key: TierKey;
  readonly name: string;
  readonly reference: string;
  readonly ciCpuThrottle: number;
  readonly targetFps: number;
  readonly renderScale: { readonly min: number; readonly max: number };
  readonly shadows: string | false;
  readonly particles: string;
}

export const TIERS: readonly TierSpec[] = [
  {
    id: 'TIER-low',
    key: 'low',
    name: "entrada",
    reference: "android de entrada, ~4 GB RAM, GPU integrada modesta",
    ciCpuThrottle: 6,
    targetFps: 30,
    renderScale: { min: 0.5, max: 0.8 },
    shadows: false,
    particles: "minimal",
  },
  {
    id: 'TIER-mid',
    key: 'mid',
    name: "intermediário",
    reference: "android intermediário atual",
    ciCpuThrottle: 3,
    targetFps: 60,
    renderScale: { min: 0.7, max: 1 },
    shadows: "baked_only",
    particles: "normal",
  },
  {
    id: 'TIER-high',
    key: 'high',
    name: "topo de linha",
    reference: "iphone recente / android flagship",
    ciCpuThrottle: 1,
    targetFps: 60,
    renderScale: { min: 0.9, max: 1 },
    shadows: "baked_plus_contact",
    particles: "rich",
  },
];

/** Iguais em TODO tier. Escalar qualquer um quebra determinismo ou justiça. */
export const INVARIANT_BUDGETS = {
  'BUD-tick': { metric: "tick do servidor", value: 30, unit: "Hz" },
  'BUD-snapshot': { metric: "taxa de snapshot", value: 15, unit: "Hz" },
  'BUD-interp': { metric: "buffer de interpolação, valor base", value: 100, unit: "ms" },
  'BUD-interp-max': { metric: "teto do buffer de interpolação adaptativo", value: 200, unit: "ms" },
  'BUD-interp-window': { metric: "janela de observação de jitter", value: 2000, unit: "ms" },
  'BUD-interp-rate': { metric: "desvio máximo do relógio de interpolação", value: 5, unit: "%" },
  'BUD-body-handoff': { metric: "janela de mistura na troca de base de tempo do corpo", value: 200, unit: "ms" },
  'BUD-tolerated-rtt': { metric: "rtt tolerado com predição", value: 250, unit: "ms" },
  'BUD-gameplay-cull': { metric: "distância de cull de gameplay", value: 40, unit: "unidades de mundo" },
  'BUD-input-rate': { metric: "taxa de input enviada pelo cliente", value: 30, unit: "Hz" },
  'BUD-recon-error': { metric: "correção de reconciliação do próprio avatar", value: 0.4, unit: "unidades de mundo" },
} as const;

/** Escalam por tier. Verificados em CI uma vez por tier. */
export const TIER_BUDGETS = {
  'BUD-frame-time': { metric: "frame time p95", unit: "ms", low: { target: 33.3, fail: 40 }, mid: { target: 16.6, fail: 22 }, high: { target: 16.6, fail: 20 } },
  'BUD-js-frame': { metric: "js por frame", unit: "ms", low: { target: 8, fail: 12 }, mid: { target: 4, fail: 6 }, high: { target: 3, fail: 5 } },
  'BUD-draw-calls': { metric: "draw calls", unit: "count", low: { target: 35, fail: 60 }, mid: { target: 60, fail: 120 }, high: { target: 90, fail: 160 } },
  'BUD-triangles': { metric: "triângulos visíveis", unit: "count", low: { target: 40000, fail: 70000 }, mid: { target: 100000, fail: 150000 }, high: { target: 180000, fail: 260000 } },
  'BUD-shaders': { metric: "shaders únicos", unit: "count", low: { target: 8, fail: 12 }, mid: { target: 10, fail: 15 }, high: { target: 14, fail: 20 } },
  'BUD-vram': { metric: "texturas em VRAM", unit: "MB", low: { target: 32, fail: 48 }, mid: { target: 64, fail: 96 }, high: { target: 110, fail: 160 } },
  'BUD-heap': { metric: "heap js", unit: "MB", low: { target: 140, fail: 200 }, mid: { target: 200, fail: 300 }, high: { target: 260, fail: 380 } },
  'BUD-bandwidth-down': { metric: "banda por jogador, servidor→cliente", unit: "KB/s", low: { target: 8, fail: 16 }, mid: { target: 10, fail: 20 }, high: { target: 12, fail: 24 } },
} as const;

/** Custo de entrada, independente de tier. */
export const ENTRY_BUDGETS = {
  'BUD-bundle': { metric: "js inicial gzip", target: 350, fail: 500, unit: "KB" },
  'BUD-ttfm': { metric: "tempo até jogar em 4G", target: 6, fail: 10, unit: "s" },
} as const;

/** Por asset, verificados no build do asset (scripts/build-assets.ts). */
export const ASSET_BUDGETS = {
  'BUD-character-triangles': { metric: "triângulos de um personagem, malha de runtime", target: 4000, fail: 6000, unit: "count" },
  'BUD-character-bytes': { metric: "arquivo .glb de um personagem, sem compressão de transporte", target: 100, fail: 160, unit: "KB" },
  'BUD-character-anim-bytes': { metric: "arquivo .glb de um personagem ANIMADO, sem compressão de transporte", target: 200, fail: 300, unit: "KB" },
} as const;

export type TierBudgetId = keyof typeof TIER_BUDGETS;
export type InvariantBudgetId = keyof typeof INVARIANT_BUDGETS;
export type EntryBudgetId = keyof typeof ENTRY_BUDGETS;
export type AssetBudgetId = keyof typeof ASSET_BUDGETS;

export function tierSpec(key: TierKey): TierSpec {
  const found = TIERS.find((tier) => tier.key === key);
  if (found === undefined) throw new Error(`tier desconhecido: ${key}`);
  return found;
}

/** Limite de falha de um orçamento no tier dado. */
export function failLimit(id: TierBudgetId, key: TierKey): number {
  return TIER_BUDGETS[id][key].fail;
}

/** Alvo de um orçamento no tier dado. */
export function targetOf(id: TierBudgetId, key: TierKey): number {
  return TIER_BUDGETS[id][key].target;
}
